import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ticketAPI, complaintTypeAPI } from '../services/api';
import { FiPlus, FiLogOut, FiSearch, FiFilter, FiGrid, FiList, FiClock } from 'react-icons/fi';
import './Dashboard.css';

const Dashboard = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState(['open', 'in-progress']); // Default: only open and in-progress
  const [selectedComplaintTypes, setSelectedComplaintTypes] = useState(['all']); // Multi-select complaint types
  const [complaintTypes, setComplaintTypes] = useState([]); // Available complaint types
  const [viewMode, setViewMode] = useState('list'); // Default to list view
  const [ticketFilter, setTicketFilter] = useState('all'); // 'all' or 'my-tickets'
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Load complaint types on mount
  useEffect(() => {
    fetchComplaintTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load filters from URL after component mounts (and whenever URL changes)
  useEffect(() => {
    const statusParam = searchParams.get('status');
    const complaintTypeParam = searchParams.get('complaintType');
    const ticketFilterParam = searchParams.get('ticketFilter');
    const viewModeParam = searchParams.get('viewMode');
    const searchParam = searchParams.get('search');

    if (statusParam) {
      setSelectedStatuses(statusParam.split(','));
    }
    if (complaintTypeParam) {
      const types = complaintTypeParam.split(',');
      setSelectedComplaintTypes(types);
      console.log('Restoring complaint type filter from URL:', types);
    }
    if (ticketFilterParam) {
      setTicketFilter(ticketFilterParam);
    }
    if (viewModeParam) {
      setViewMode(viewModeParam);
    }
    if (searchParam) {
      setSearchTerm(searchParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, ticketFilter, selectedComplaintTypes]); // Re-fetch when filters change

  // Update URL when filters change (but avoid updating on initial load from URL)
  useEffect(() => {
    // Build the params object
    const params = new URLSearchParams();

    // Always save status to URL
    if (selectedStatuses.length > 0) {
      params.set('status', selectedStatuses.join(','));
    }

    // Save complaint types if not 'all'
    if (selectedComplaintTypes.length > 0 && !selectedComplaintTypes.includes('all')) {
      params.set('complaintType', selectedComplaintTypes.join(','));
    }

    if (ticketFilter !== 'all') {
      params.set('ticketFilter', ticketFilter);
    }
    if (viewMode !== 'list') {
      params.set('viewMode', viewMode);
    }
    if (searchTerm) {
      params.set('search', searchTerm);
    }

    // Only update URL if params have actually changed
    const currentParams = searchParams.toString();
    const newParams = params.toString();

    if (currentParams !== newParams) {
      setSearchParams(params, { replace: true });
      console.log('Updating URL with filters:', {
        status: selectedStatuses,
        complaintType: selectedComplaintTypes,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatuses, selectedComplaintTypes, ticketFilter, viewMode, searchTerm]);

  const fetchComplaintTypes = async () => {
    try {
      const response = await complaintTypeAPI.getAll();
      setComplaintTypes(response.data);
    } catch (error) {
      console.error('Error fetching complaint types:', error);
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {};

      // Only filter by user if "My Tickets" is selected
      if (ticketFilter === 'my-tickets') {
        params.user_id = user.id;
      }

      // Note: Backend doesn't support multi-status or multi-type filtering yet, so we'll fetch all and filter client-side
      // If you want to add backend support, modify the backend API to accept multiple statuses

      const response = await ticketAPI.getAllTickets(params);
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const filteredTickets = tickets.filter(ticket => {
    // Filter by search term
    const matchesSearch = ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.description.toLowerCase().includes(searchTerm.toLowerCase());

    // Filter by selected statuses
    const matchesStatus = selectedStatuses.length === 0 || selectedStatuses.includes(ticket.status);

    // Filter by selected complaint types
    const matchesComplaintType = selectedComplaintTypes.includes('all') ||
      selectedComplaintTypes.includes(ticket.complaint_type_id.toString());

    return matchesSearch && matchesStatus && matchesComplaintType;
  });

  // Toggle status tab
  const toggleStatus = (status) => {
    setSelectedStatuses(prev => {
      if (prev.includes(status)) {
        return prev.filter(s => s !== status);
      } else {
        return [...prev, status];
      }
    });
  };

  // Toggle complaint type tab
  const toggleComplaintType = (typeId) => {
    setSelectedComplaintTypes(prev => {
      // If clicking "All"
      if (typeId === 'all') {
        return ['all'];
      }

      // Remove "all" if it was selected
      const withoutAll = prev.filter(id => id !== 'all');

      // Toggle the clicked type
      if (withoutAll.includes(typeId)) {
        const filtered = withoutAll.filter(id => id !== typeId);
        // If no types selected, default back to "all"
        return filtered.length === 0 ? ['all'] : filtered;
      } else {
        return [...withoutAll, typeId];
      }
    });
  };

  // Calculate time elapsed for a ticket
  const calculateTimeElapsed = (createdAt, status, statusHistory) => {
    const now = new Date();
    const created = new Date(createdAt);

    // For resolved/closed tickets, find when they were resolved/closed
    if (status === 'resolved' || status === 'closed') {
      const resolvedHistory = statusHistory?.find(h => h.new_status === 'resolved' || h.new_status === 'closed');
      if (resolvedHistory) {
        const resolvedDate = new Date(resolvedHistory.changed_at);
        const diffMs = resolvedDate - created;
        return formatTimeDifference(diffMs);
      }
    }

    // For open/in-progress tickets, calculate from creation time to now
    if (status === 'open' || status === 'in-progress') {
      const diffMs = now - created;
      return formatTimeDifference(diffMs);
    }

    return null;
  };

  // Format time difference
  const formatTimeDifference = (diffMs) => {
    const diffMinutes = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffDays > 0) {
      const remainingHours = diffHours % 24;
      return `${diffDays}d ${remainingHours}h`;
    } else if (diffHours > 0) {
      const remainingMinutes = diffMinutes % 60;
      return `${diffHours}h ${remainingMinutes}m`;
    } else {
      return `${diffMinutes}m`;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status, isWithdrawn) => {
    if (isWithdrawn) return 'badge-withdrawn';
    switch (status) {
      case 'open':
        return 'badge-open';
      case 'in-progress':
        return 'badge-in-progress';
      case 'resolved':
        return 'badge-resolved';
      case 'closed':
        return 'badge-closed';
      default:
        return 'badge-open';
    }
  };

  return (
    <div className="dashboard-container">
      <header className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <h1 className="dashboard-title">Ticket Dashboard</h1>
              <p className="dashboard-subtitle">Welcome back, {user.full_name}</p>
            </div>
            <div className="header-right">
              {user.role === 'admin' && (
                <>
                  <button
                    onClick={() => navigate('/admin/invites')}
                    className="btn btn-secondary"
                    style={{ marginRight: '8px' }}
                  >
                    Generate Invites
                  </button>
                  <button
                    onClick={() => navigate('/admin/password-resets')}
                    className="btn btn-secondary"
                    style={{ marginRight: '8px' }}
                  >
                    Password Resets
                  </button>
                </>
              )}
              <button onClick={() => navigate('/create-ticket')} className="btn btn-primary">
                <FiPlus /> New Ticket
              </button>
              <button onClick={handleLogout} className="btn btn-secondary">
                <FiLogOut /> Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="dashboard-content container">
        {/* Status Filter - Tab-style buttons */}
        <div className="status-filter-bar">
          <div className="status-filter-label">
            <FiFilter /> Filter by Status:
          </div>
          <div className="status-tabs">
            {['open', 'in-progress', 'resolved', 'closed'].map(status => (
              <button
                key={status}
                className={`status-tab ${selectedStatuses.includes(status) ? 'active' : ''}`}
                onClick={() => toggleStatus(status)}
              >
                {status === 'in-progress' ? 'In Progress' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Complaint Type Filter - Tab-style buttons */}
        <div className="type-filter-bar">
          <div className="type-filter-label">
            <FiFilter /> Filter by Type:
          </div>
          <div className="type-tabs">
            <button
              className={`type-tab ${selectedComplaintTypes.includes('all') ? 'active' : ''}`}
              onClick={() => toggleComplaintType('all')}
            >
              All Types
            </button>
            {complaintTypes.map(type => (
              <button
                key={type.id}
                className={`type-tab ${selectedComplaintTypes.includes(type.id.toString()) ? 'active' : ''}`}
                onClick={() => toggleComplaintType(type.id.toString())}
              >
                {type.type_name}
              </button>
            ))}
          </div>
        </div>

        <div className="filters-section">
          <div className="search-box">
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search tickets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="ticket-filter-toggle">
            <button
              className={`filter-button ${ticketFilter === 'all' ? 'active' : ''}`}
              onClick={() => setTicketFilter('all')}
              title="Show all team tickets"
            >
              👥 All Tickets
            </button>
            <button
              className={`filter-button ${ticketFilter === 'my-tickets' ? 'active' : ''}`}
              onClick={() => setTicketFilter('my-tickets')}
              title="Show only my tickets"
            >
              👤 My Tickets
            </button>
          </div>

          <div className="view-toggle">
            <button
              className={`view-button ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="List View"
            >
              <FiList />
            </button>
            <button
              className={`view-button ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View (Tiles)"
            >
              <FiGrid />
            </button>
          </div>
        </div>

        {loading ? (
          <div className="spinner"></div>
        ) : filteredTickets.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">🎫</div>
            <h3>No tickets found</h3>
            <p>Create your first ticket to get started</p>
            <button onClick={() => navigate('/create-ticket')} className="btn btn-primary">
              <FiPlus /> Create Ticket
            </button>
          </div>
        ) : (
          <div className={viewMode === 'grid' ? 'tickets-grid' : 'tickets-list'}>
            {filteredTickets.map((ticket) => (
              <div
                key={ticket.id}
                className={`ticket-card card ${ticket.is_withdrawn ? 'withdrawn' : ''} ${viewMode === 'list' ? 'list-view' : ''}`}
                onClick={() => navigate(`/ticket/${ticket.id}`)}
              >
                <div className="ticket-header">
                  <span className={`ticket-id ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                    {ticket.ticket_id}
                  </span>
                  <span className={`badge ${getStatusBadge(ticket.status, ticket.is_withdrawn)}`}>
                    {ticket.is_withdrawn ? 'Withdrawn' : ticket.status}
                  </span>
                </div>

                {viewMode === 'list' && (
                  <>
                    <div className="ticket-type">
                      {ticket.complaint_type_name}
                    </div>
                    <div className="ticket-creator">
                      By: {ticket.user_name}
                    </div>
                    {/* Time tracking info */}
                    {(ticket.status === 'open' || ticket.status === 'in-progress') && (
                      <div className="ticket-time-elapsed">
                        <FiClock className="time-icon" />
                        <span className="time-label">Open for:</span>
                        <span className="time-value">{calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}</span>
                      </div>
                    )}
                    {(ticket.status === 'resolved' || ticket.status === 'closed') && (
                      <div className="ticket-time-resolved">
                        <FiClock className="time-icon" />
                        <span className="time-label">Resolved in:</span>
                        <span className="time-value">{calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}</span>
                      </div>
                    )}
                  </>
                )}

                <h3 className={`ticket-title ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.title}
                </h3>
                
                <p className={`ticket-description ${ticket.is_withdrawn ? 'withdrawn-text' : ''}`}>
                  {ticket.description.substring(0, viewMode === 'list' ? 150 : 100)}
                  {ticket.description.length > (viewMode === 'list' ? 150 : 100) ? '...' : ''}
                </p>

                {viewMode === 'list' && (
                  <div className="ticket-created">
                    Created: {formatDate(ticket.created_at)}
                  </div>
                )}

                {viewMode !== 'list' && (
                  <div className="ticket-meta">
                    <div className="meta-item">
                      <span className="meta-label">Type:</span>
                      <span className="meta-value">{ticket.complaint_type_name}</span>
                    </div>
                    <div className="meta-item">
                      <span className="meta-label">Created:</span>
                      <span className="meta-value">{formatDate(ticket.created_at)}</span>
                    </div>
                    {/* Time tracking info for grid view */}
                    {(ticket.status === 'open' || ticket.status === 'in-progress') && (
                      <div className="meta-item time-tracking">
                        <span className="meta-label"><FiClock /> Open for:</span>
                        <span className="meta-value time-elapsed">{calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}</span>
                      </div>
                    )}
                    {(ticket.status === 'resolved' || ticket.status === 'closed') && (
                      <div className="meta-item time-tracking">
                        <span className="meta-label"><FiClock /> Resolved in:</span>
                        <span className="meta-value time-resolved">{calculateTimeElapsed(ticket.created_at, ticket.status, ticket.status_history)}</span>
                      </div>
                    )}
                  </div>
                )}

                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="ticket-attachments">
                    <span className="attachment-count">
                      📎 {ticket.attachments.length} attachment(s)
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;